/**
 * V199: 동적 z-index 지원 Dialog 컴포넌트
 *
 * ModalStackContext와 통합하여 가장 나중에 열린 모달이
 * 항상 최상위에 표시되도록 함
 */
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useModalStack } from "@/contexts/ModalStackContext"

// V199: 동적 z-index를 위한 Context
const DialogZIndexContext = React.createContext<number>(200)

// V199: Dialog Root를 확장하여 open 상태 추적
interface DialogProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  children: React.ReactNode
}

const Dialog = ({ children, open, ...props }: DialogProps) => {
  const { zIndex } = useModalStack(open ?? false)

  return (
    <DialogPrimitive.Root open={open} {...props}>
      <DialogZIndexContext.Provider value={zIndex}>
        {children}
      </DialogZIndexContext.Provider>
    </DialogPrimitive.Root>
  )
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// V199: 동적 z-index 적용된 Overlay
// V201: payple-pending 클래스 기반 숨김 처리 (CSS 선택자 매칭 문제 해결)
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    'data-payple-pending'?: boolean | string
  }
>(({ className, style, 'data-payple-pending': payplePending, ...props }, ref) => {
  const zIndex = React.useContext(DialogZIndexContext)
  const isPending = payplePending === true || payplePending === 'true'

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        // V201: Payple 인증 중 숨김 클래스 적용
        isPending && "payple-pending-hide",
        className
      )}
      style={{ ...style, zIndex: isPending ? 0 : zIndex }}
      {...props}
    />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// V199: 동적 z-index 적용된 Content
// V201: payple-pending 클래스 기반 숨김 처리 (CSS 선택자 매칭 문제 해결)
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, style, ...props }, ref) => {
  const zIndex = React.useContext(DialogZIndexContext)

  // Payple 인증 중 Overlay와 Content 모두 숨김 처리
  const payplePending = props['data-payple-pending']
  const isPending = payplePending === true || payplePending === 'true'

  return (
    <DialogPortal>
      <DialogOverlay data-payple-pending={payplePending} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "!fixed !left-[50%] !top-[50%] grid w-full max-w-lg !translate-x-[-50%] !translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[90vh] overflow-y-auto",
          // V201: Payple 인증 중 숨김 클래스 적용
          isPending && "payple-pending-hide",
          className
        )}
        style={{ ...style, zIndex: isPending ? 0 : zIndex }}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
